import { BroadcastEventType } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import {
  DescriptionChangedAction,
  DiscoverableChangedAction,
  EndDateChangedAction,
  EndTimeChangedAction,
  EventSelectedForEditingAction,
  ModalResetAction,
  NameChangedAction,
  PresentersChangedAction,
  ProducersChangedAction,
  ProductionTypeChangedAction,
  ReplyToConversationChangedAction,
  StartDateChangedAction,
  StartTimeChangedAction,
  ThreadStarterAdminModeratedChangedAction,
  ThreadStarterRestrictionChangedAction,
} from '../../domains/createAndEditEventModal/actions.types';

import { CreateAndEditEventModal, TwentyFourHour } from './types';

export type CreateAndEditEventModalAction =
  | DescriptionChangedAction
  | DiscoverableChangedAction
  | EndDateChangedAction
  | EndTimeChangedAction
  | EventSelectedForEditingAction
  | ModalResetAction
  | NameChangedAction
  | PresentersChangedAction
  | ProducersChangedAction
  | ProductionTypeChangedAction
  | ReplyToConversationChangedAction
  | StartDateChangedAction
  | StartTimeChangedAction
  | ThreadStarterRestrictionChangedAction
  | ThreadStarterAdminModeratedChangedAction;

export type GetInitialState = () => CreateAndEditEventModal;
export const getInitialState: GetInitialState = () => {
  const minuteInMS = 1000 * 60;
  const startTime = getNextThirtyMinuteIntervalTime();
  const endTime = startTime + 30 * minuteInMS;

  return {
    eventDetails: {
      description: '',
      endTime,
      name: '',
      presenters: [],
      producers: [],
      startTime,
      changed: {},
      isThreadStarterRestricted: false,
      isThreadStarterAdminModerated: false,
      isReplyToConversationEnabled: true,
    },
    productionType: BroadcastEventType.YAMMER_QUICKSTART,
    editingBroadcastId: undefined,
  };
};

const getNextThirtyMinuteIntervalTime = () => {
  const date = new Date(Date.now());
  const newMinutes = date.getMinutes() < 30 ? 30 : 60;

  date.setMinutes(newMinutes);

  return date.getTime();
};

const getEventTimestampWithNewTime = (prevTimestamp: number, hours: TwentyFourHour, minutes: number) => {
  const updatedDate = new Date(prevTimestamp);
  updatedDate.setHours(hours);
  updatedDate.setMinutes(minutes);

  return updatedDate.getTime();
};

const getEventTimestampWithNewDate = (prevTimestamp: number, newDateTimestamp: number): number => {
  const newDateTime = new Date(newDateTimestamp);
  const updatedDate = new Date(prevTimestamp);
  updatedDate.setFullYear(newDateTime.getFullYear(), newDateTime.getMonth(), newDateTime.getDate());

  return updatedDate.getTime();
};

const isEndTimeBeforeStartTime = (startTime: number, endTime: number): boolean => endTime <= startTime;

const getUpdatedStartDate = (endTime: number): number => {
  const endDate = new Date(endTime);
  endDate.setMinutes(endDate.getMinutes() - 30);

  return endDate.getTime();
};

const getUpdatedEndDate = (startTime: number): number => {
  const startDate = new Date(startTime);
  startDate.setMinutes(startDate.getMinutes() + 30);

  return startDate.getTime();
};

const getStateWithUpdatedEndTime = (state: CreateAndEditEventModal, { payload }: EndTimeChangedAction) => {
  const newEndTime = getEventTimestampWithNewTime(state.eventDetails.endTime, payload.hours, payload.minutes);

  return {
    ...state,
    eventDetails: {
      ...state.eventDetails,
      endTime: newEndTime,
      startTime: isEndTimeBeforeStartTime(state.eventDetails.startTime, newEndTime)
        ? getUpdatedStartDate(newEndTime)
        : state.eventDetails.startTime,
    },
  };
};

const getStateWithUpdatedStartTime = (state: CreateAndEditEventModal, { payload }: StartTimeChangedAction) => {
  const newStartTime = getEventTimestampWithNewTime(state.eventDetails.startTime, payload.hours, payload.minutes);

  return {
    ...state,
    eventDetails: {
      ...state.eventDetails,
      startTime: newStartTime,
      endTime: isEndTimeBeforeStartTime(newStartTime, state.eventDetails.endTime)
        ? getUpdatedEndDate(newStartTime)
        : state.eventDetails.endTime,
    },
  };
};

const getStateWithUpdatedStartDate = (state: CreateAndEditEventModal, action: StartDateChangedAction) => {
  const newStartTime = getEventTimestampWithNewDate(state.eventDetails.startTime, action.payload);

  return {
    ...state,
    eventDetails: {
      ...state.eventDetails,
      startTime: newStartTime,
      endTime: isEndTimeBeforeStartTime(newStartTime, state.eventDetails.endTime)
        ? getUpdatedEndDate(newStartTime)
        : state.eventDetails.endTime,
    },
  };
};

const getStateWithUpdatedEndDate = (state: CreateAndEditEventModal, action: EndDateChangedAction) => {
  const newEndTime = getEventTimestampWithNewDate(state.eventDetails.endTime, action.payload);

  return {
    ...state,
    eventDetails: {
      ...state.eventDetails,
      endTime: newEndTime,
      startTime: isEndTimeBeforeStartTime(state.eventDetails.startTime, newEndTime)
        ? getUpdatedStartDate(newEndTime)
        : state.eventDetails.startTime,
    },
  };
};

const getStateWithUpdatedPresenters = (state: CreateAndEditEventModal, action: PresentersChangedAction) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    presenters: action.payload,
  },
});

const getStateWithUpdatedProducers = (state: CreateAndEditEventModal, action: ProducersChangedAction) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    producers: action.payload,
  },
});

const getStateWithUpdatedProductionType = (state: CreateAndEditEventModal, action: ProductionTypeChangedAction) => ({
  ...state,
  productionType: action.payload,
});

const getStateWithUpdatedDescription = (state: CreateAndEditEventModal, action: DescriptionChangedAction) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    description: action.payload,
  },
});

const getStateWithUpdatedName = (state: CreateAndEditEventModal, action: NameChangedAction) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    name: action.payload,
    changed: {
      ...state.eventDetails.changed,
      name: true,
    },
  },
});

const getStateWithUpdatedDiscoverable = (state: CreateAndEditEventModal, action: DiscoverableChangedAction) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    isDiscoverable: action.payload,
  },
});

const getStateWithUpdatedIsThreadStarterRestricted = (
  state: CreateAndEditEventModal,
  action: ThreadStarterRestrictionChangedAction
) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    isThreadStarterRestricted: action.payload,
  },
});

const getStateWithUpdatedIsThreadStarterAdminModerated = (
  state: CreateAndEditEventModal,
  action: ThreadStarterAdminModeratedChangedAction
) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    isThreadStarterAdminModerated: action.payload,
  },
});

const getStateWithUpdatedIsReplyToConversationEnabled = (
  state: CreateAndEditEventModal,
  action: ReplyToConversationChangedAction
) => ({
  ...state,
  eventDetails: {
    ...state.eventDetails,
    isReplyToConversationEnabled: action.payload,
  },
});

const getStateWithEventDetails = (state: CreateAndEditEventModal, { payload }: EventSelectedForEditingAction) => ({
  ...state,
  eventDetails: {
    changed: {},
    description: payload.description ?? '',
    endTime: payload.endAt,
    isDiscoverable: payload.isDiscoverable,
    name: payload.title,
    presenters: payload.presenterIds,
    producers: payload.producerIds,
    startTime: payload.startAt,
    isThreadStarterRestricted: payload.isThreadStarterRestricted,
    isThreadStarterAdminModerated: payload.isThreadStarterAdminModerated,
    isReplyToConversationEnabled: payload.isReplyToConversationEnabled,
  },
  editingBroadcastId: payload.broadcastId,
  productionType: payload.eventType,
});

const reducer: Reducer<CreateAndEditEventModal, CreateAndEditEventModalAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case 'CREATE_AND_EDIT_EVENT_MODAL.PRODUCTION_TYPE_CHANGED':
      return getStateWithUpdatedProductionType(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.DISCOVERABLE_CHANGED':
      return getStateWithUpdatedDiscoverable(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.DESCRIPTION_CHANGED':
      return getStateWithUpdatedDescription(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.END_TIME_CHANGED':
      return getStateWithUpdatedEndTime(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.NAME_CHANGED':
      return getStateWithUpdatedName(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.PRESENTERS_CHANGED':
      return getStateWithUpdatedPresenters(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.PRODUCERS_CHANGED':
      return getStateWithUpdatedProducers(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.START_DATE_CHANGED':
      return getStateWithUpdatedStartDate(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.END_DATE_CHANGED':
      return getStateWithUpdatedEndDate(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.START_TIME_CHANGED':
      return getStateWithUpdatedStartTime(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.MODAL_RESET':
      return getInitialState();
    case 'CREATE_AND_EDIT_EVENT_MODAL.EVENT_SELECTED_FOR_EDITING':
      return getStateWithEventDetails(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.THREAD_STARTER_RESTRICTION_CHANGED':
      return getStateWithUpdatedIsThreadStarterRestricted(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.THREAD_STARTER_ADMIN_MODERATED_CHANGED':
      return getStateWithUpdatedIsThreadStarterAdminModerated(state, action);
    case 'CREATE_AND_EDIT_EVENT_MODAL.REPLY_TO_CONVERSATION_CHANGED':
      return getStateWithUpdatedIsReplyToConversationEnabled(state, action);
    default:
      return state;
  }
};

export default reducer;
